import { FC, useState, useRef } from "react";
import Slider from "react-slick";
import useMediaQuery from "@mui/material/useMediaQuery";
import Icon from "@Components/ui/icon/Icon";
import CmsTextEditorContent from "@Libs/contentful/components/cms-text-editor/CmsTextEditor";
import {
  LazyType,
  UserReviewsProps,
  IArrowProps,
  StarProps,
  IRefProps,
} from "@Types/UserReviews";
import { useAppSelector } from "@wff/store/hooks";
import { localeSelector } from "@wff/store/localeSlice";
import Image from "components/Image";
import classes from "./UserReviews.module.scss";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const UserReviews: FC<UserReviewsProps> = (props) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef<IRefProps>(null);
  const currentLocale = useAppSelector(localeSelector)?.languageCode ?? "en-US";
  const isMobile = useMediaQuery("(max-width:30em)");

  const goToSlide = (slide: number) => {
    if (sliderRef.current) sliderRef.current.slickGoTo(slide);
  };

  const SamplePrevArrow = (props: IArrowProps) => {
    const { currentSlide } = props;
    const arrowClass = `${classes["arrow"] + " " + classes["arrow-left"]}`;

    const handleLeftArrowClick = () => {
      if (currentSlide !== undefined && currentSlide === 0) goToSlide(-1);
      else if (currentSlide && currentSlide > 0) goToSlide(currentSlide - 1);
    };

    return (
      <div className={arrowClass} onClick={handleLeftArrowClick}>
        <Icon iconName="arrow-left-thin" />
      </div>
    );
  };

  const SampleNextArrow = (props: IArrowProps) => {
    const { slideCount, currentSlide } = props;
    const arrowClass = `${classes["arrow"]} ${classes["arrow-right"]}`;

    const handleRightArrowClick = () => {
      if (slideCount && currentSlide !== undefined && currentSlide < slideCount)
        goToSlide(currentSlide + 1);
    };

    return (
      <div className={arrowClass} onClick={handleRightArrowClick}>
        <Icon iconName="arrow-right-thin" />
      </div>
    );
  };

  const Star = ({ marked, starId, readonly, size, color }: StarProps) => {
    return (
      <span
        data-star-id={starId}
        className={classes.star}
        style={{
          fontSize: size,
          cursor: readonly ? "default" : "pointer",
          color: color,
        }}
        role="button"
      >
        {marked ? "\u2605" : "\u2606"}
      </span>
    );
  };

  const settings = {
    beforeChange: (prev: number, next: number) => {
      if (next !== prev) setCurrentSlide(next);
    },
    infinite: true,
    lazyLoad: "progressive" as LazyType,
    swipeDirection: "down",
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: true,
    adaptiveHeight: false,
    prevArrow: <SamplePrevArrow />,
    nextArrow: <SampleNextArrow />,
    className: classes["section-slider"],
    initialSlide: currentSlide,
    responsive: [
      {
        breakpoint: 1100,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  const items = props.reviews.items;
  if (!items?.length) return null;

  return (
    <section className="section">
      <div
        className={`section__content__carousel ${classes["reviews-section-content"]}`}
      >
        <h2 className={classes["title"]}>{props.name ? props.name : ""}</h2>
        <Slider {...settings} ref={sliderRef}>
          {items?.map((item, index) => (
            <div
              key={index}
              className={`
              ${
                classes["product-review-tile"] +
                " " +
                classes["product-review-tile--featured"] +
                " " +
                classes["product-review-tile--gallery"] +
                " " +
                classes["product-review-tile__padding"]
              }`}
            >
              <a
                href={item.link ? `/${currentLocale}/${item.link}` : item.link}
              >
                <div className={classes["product-review-tile__container"]}>
                  <div className={classes["product-review-tile__gallery"]}>
                    <div
                      className={
                        classes["product-review-tile__media-container"]
                      }
                    >
                      <img
                        src={
                          item.cookedOrRaw === "cooked"
                            ? `${item.cookedImage.url}?fm=webp&w=1920&h=1080`
                            : `${item.rawImage.url}?fm=webp&w=1920&h=1080`
                        }
                        alt="Product Image"
                        className={classes["product-review-tile__image"]}
                      />
                      <h3 className={classes["product-review-tile__title"]}>
                        {item.productName ? item.productName : ""}
                      </h3>
                    </div>
                  </div>
                  <div className={classes["product-review-tile__wrapper"]}>
                    <div
                      className={classes["product-review-tile__wrapper-logo"]}
                    >
                      <Image
                        src="/flame_solid.png"
                        alt="Flame Logo"
                        layout="fill"
                        objectFit="contain"
                      />
                      <div
                        className={
                          classes["product-review-tile__wrapper-logo-text"]
                        }
                      >
                        {item.username[0]}
                      </div>
                    </div>
                    <div>
                      <div className={classes["product-review-tile__footer"]}>
                        <div
                          className={classes["product-review-tile__username"]}
                        >
                          {item.username ? item.username : ""}
                        </div>
                      </div>
                      <div
                        className={classes["product-review-tile__star-rating"]}
                      >
                        {Array.from({ length: 5 }, (_, i) => (
                          <Star
                            size="1.05rem"
                            color="#FEC600"
                            readonly={true}
                            starId={i + 1}
                            key={`star_${i + 1}`}
                            marked={item.stars >= i + 1}
                          />
                        ))}
                      </div>
                      {!isMobile && (
                        <div
                          className={
                            classes["product-review-tile__review-text"]
                          }
                        >
                          <CmsTextEditorContent
                            content={item.text ? item.text : ""}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  {isMobile && (
                    <div
                      className={
                        classes["product-review-tile__review-text-mobile"]
                      }
                    >
                      <CmsTextEditorContent
                        content={item.text ? item.text : ""}
                      />
                    </div>
                  )}
                </div>
              </a>
            </div>
          ))}
        </Slider>
      </div>
    </section>
  );
};

export default UserReviews;
